import { useIntl } from 'react-intl';
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  FormInstance,
  Tooltip,
  Divider,
} from 'antd';

import FloatingFormItem from '@/components/_common/FloatingFormItem';
import {
  BankAccountInfiniteSelectField,
  DocumentTypesInfiniteSelectField,
  InputDate,
  InputDecimal,
  InputSearch,
  PlanAccountsSelect,
} from '@/components/_common';
import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
  useDisclosure,
} from '@/hooks';
import {
  PlanAccountDestination,
  PlanAccountHierarchy,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import moment from 'moment';
import SituationFormField from '@/components/Movements/DirectSale/SituationFormField';
import EmployeesSelectedModal from '@/components/_common/EmployeesSelectedModal';
import SuppliersSelectedModal from '@/components/_common/SuppliersSelectedModal';
import { SupplierReduced } from '@/core/domain/people/Suppliers/Suppliers.types';
import { Employee } from '@/core/domain/enterprise/Employees/Employees.types';
import {
  AccountPayableReleaseFormValues,
  Creditor,
  Installment,
  InstallmentSituation,
} from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import CompaniesInfiniteSelectField from '@/components/_common/CompaniesInfiniteSelectField';
import CostCenterInfiniteSelectField from '@/components/_common/CostCenterInfiniteSelectField';
import { useSupplier } from '@/core/domain/people/Suppliers/Suppliers.hooks';
import AccountPayableObservationList from './AccountPayableObservationList';
import formatterPrice from '@/utils/number/formatterPrice';
import useDefaultMinLengthNegativeFieldError from '@/hooks/useDefaultMinLengthNegativeFieldError';
import { useCompanySettings } from '@/core/domain/enterprise/Companies/Companies.hooks';
import { useAccountPayableContext } from '@/pages/FinancialPages/BillToPayPage/ReleasesPage/ReleasesDetailsPage';

interface Props {
  form: FormInstance<AccountPayableReleaseFormValues>;
  isEdit?: boolean;
  installment?: Installment;
}

function ReleaseForm({ form, isEdit, installment }: Props) {
  const intl = useIntl();
  const { accountPayable } = useAccountPayableContext();

  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();
  const defaultMinLengthNegativeFieldError =
    useDefaultMinLengthNegativeFieldError();

  const [modalEmployeeVisible, modalEmployeeAction] = useDisclosure();
  const [modalSupplierVisible, modalSupplierAction] = useDisclosure();

  const supplierId = Form.useWatch('supplierId', form);
  const installmentValue = Form.useWatch('installmentValue', form);
  const discountIncrease = Form.useWatch('discountIncrease', form);

  const { data: supplierData } = useSupplier(supplierId);
  const { data: companySettings } = useCompanySettings(
    accountPayable?.companyId,
  );

  const disabled =
    installment?.situation === InstallmentSituation.settled ||
    installment?.situation === InstallmentSituation.canceled;

  const handleFormValuesChange = (
    _valuesChanged: AccountPayableReleaseFormValues,
    fieldValues: AccountPayableReleaseFormValues,
  ) => {
    form.setFieldsValue({
      totalAmount:
        (fieldValues.installmentValue ?? 0) +
        (fieldValues.discountIncrease ?? 0) -
        (fieldValues.credit ?? 0),
    });
  };

  const handleSupplierSelect = (supplier?: SupplierReduced) => {
    form.setFieldsValue({
      supplier: supplier?.name,
      supplierId: supplier?.id,
      planAccountId: supplier?.planAccountId,
    });
    modalSupplierAction.close();
  };

  const handleEmployeeSelect = (employee?: Employee) => {
    form.setFieldsValue({
      employee: employee?.name,
      employeeId: employee?.id,
    });
    modalEmployeeAction.close();
  };

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        onValuesChange={handleFormValuesChange}
        name="accountPayableGeneralForm"
      >
        <Row gutter={[16, 9]}>
          {!isEdit && (
            <>
              <Col lg={24} md={24} span={24}>
                <SituationFormField
                  initialValue={form.getFieldValue('situation')}
                />
              </Col>

              <Col span={24} md={12} lg={8}>
                <FloatingFormItem
                  name={'companyId'}
                  label={'Empresa'}
                  rules={[defaultEmptyFieldError]}
                >
                  <CompaniesInfiniteSelectField
                    allowClear
                    disabled={disabled}
                    queryEnable
                  />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={12} lg={8}>
                <FloatingFormItem
                  label={intl.formatMessage({
                    id: 'release.form.creditor',
                  })}
                  name="creditor"
                  rules={[defaultEmptyFieldError]}
                  initialValue={Creditor.supplier}
                >
                  <Select
                    allowClear
                    style={{ width: '100' }}
                    options={[
                      {
                        value: Creditor.supplier,
                        label: intl.formatMessage({
                          id: 'release.form.supplier',
                        }),
                      },
                      {
                        value: Creditor.employee,
                        label: intl.formatMessage({
                          id: 'release.form.employee',
                        }),
                      },
                    ]}
                    disabled={disabled}
                  />
                </FloatingFormItem>
              </Col>

              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const creditor = getFieldValue('creditor');

                  if (creditor === Creditor.employee) {
                    return (
                      <Col span={24} md={12} lg={8}>
                        <Form.Item hidden name="employeeId" />

                        <FloatingFormItem
                          name="employee"
                          label={intl.formatMessage({
                            id: 'release.form.employee',
                          })}
                          rules={[defaultEmptyFieldError]}
                        >
                          <InputSearch
                            inputProps={{ disabled: true }}
                            buttonProps={{
                              onClick: modalEmployeeAction.open,
                              disabled: disabled,
                            }}
                          />
                        </FloatingFormItem>
                      </Col>
                    );
                  } else {
                    return (
                      <Col span={24} md={12} lg={8}>
                        <Form.Item hidden name="supplierId" />

                        <FloatingFormItem
                          name="supplier"
                          label={intl.formatMessage({
                            id: 'release.form.supplier',
                          })}
                          rules={[defaultEmptyFieldError]}
                        >
                          <InputSearch
                            inputProps={{ disabled: true }}
                            buttonProps={{
                              onClick: modalSupplierAction.open,
                              disabled: disabled,
                            }}
                          />
                        </FloatingFormItem>
                      </Col>
                    );
                  }
                }}
              </Form.Item>

              <Col span={24} md={12} lg={6}>
                <FloatingFormItem
                  label={intl.formatMessage({
                    id: 'release.form.planAccount',
                  })}
                  name="planAccountId"
                  rules={[defaultEmptyFieldError]}
                >
                  <PlanAccountsSelect
                    queryFilter={{
                      hierarchy: PlanAccountHierarchy.Analitical,
                      destination: PlanAccountDestination.Expense,
                      active: true,
                    }}
                    disabled={disabled}
                  />
                </FloatingFormItem>
              </Col>
            </>
          )}

          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'release.form.description',
              })}
              name="description"
            >
              <Input disabled={disabled} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'release.form.documentNumber',
              })}
              name="documentNumber"
              rules={[defaultMaxLenghtFieldError(255)]}
            >
              <Input disabled={disabled} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'release.form.documentType',
              })}
              name="documentTypeId"
              rules={[defaultEmptyFieldError]}
            >
              <DocumentTypesInfiniteSelectField disabled={disabled} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'release.form.issueDate',
              })}
              name="emissionDate"
              initialValue={moment()}
              rules={[defaultEmptyFieldError]}
            >
              <InputDate disabled={disabled} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'release.form.dueDate',
              })}
              name="dueDate"
              initialValue={moment().add(1, 'months')}
              rules={[defaultEmptyFieldError]}
            >
              <InputDate disabled={disabled} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'release.form.payDay',
              })}
              name="payDate"
            >
              <InputDate disabled />
            </FloatingFormItem>
          </Col>

          {!isEdit && (
            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'release.form.costCenter',
                })}
                name="costCenterId"
              >
                <CostCenterInfiniteSelectField
                  queryFilter={{ active: true }}
                  disabled={disabled}
                />
              </FloatingFormItem>
            </Col>
          )}

          <Col span={24} md={12} lg={12}>
            <FloatingFormItem
              name="bankAccountId"
              label={intl.formatMessage({
                id: 'release.form.bankAccount',
              })}
            >
              <BankAccountInfiniteSelectField
                disabled={disabled}
                queryFilter={{ active: true }}
              />
            </FloatingFormItem>
          </Col>

          <Col xs={24} span={24} md={12} lg={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'release.form.value',
              })}
              name="installmentValue"
              rules={[
                defaultEmptyFieldError,
                {
                  type: 'number',
                  min: 0.01,
                  message:
                    'O valor da parcela não pode ser igual ou menor que zero!',
                },
                {
                  min: Form.useWatch('credit', form),
                  type: 'number',
                  message: `Valor não pode ser menor que o crédito do fornecedor`,
                },
              ]}
            >
              <InputDecimal min={0} disabled={disabled} />
            </FloatingFormItem>
          </Col>

          <Col xs={24} span={24} md={12} lg={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'release.form.increaseAndDecrease',
              })}
              name="discountIncrease"
              initialValue={0}
              rules={
                !companySettings?.allowNegativeLiquidValueInPayables
                  ? [defaultMinLengthNegativeFieldError(installmentValue * -1)]
                  : []
              }
            >
              <InputDecimal disabled={disabled} />
            </FloatingFormItem>
          </Col>

          <Tooltip
            title={`Crédito disponível: ${formatterPrice(
              supplierData?.credit || 0,
            )}`}
            trigger="click"
          >
            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                name="credit"
                label={intl.formatMessage({
                  id: 'release.form.supplierCredit',
                })}
                initialValue={0}
                rules={
                  !companySettings?.allowNegativeLiquidValueInPayables
                    ? [
                        {
                          type: 'number',
                          max: installmentValue + discountIncrease,
                          message: `Valor não pode ser maior que ${formatterPrice(
                            installmentValue + discountIncrease,
                          )}`,
                        },
                      ]
                    : []
                }
              >
                <InputSearch
                  viewIcon
                  valueMax={supplierData?.credit ?? 0}
                  inputDecimal
                  buttonProps={{
                    onClick: () =>
                      window.open(
                        `/app/registry/suppliers/${supplierId}/supplierCredit`,
                        '_blank',
                      ),
                    disabled: !supplierId || disabled,
                  }}
                  disabledInputDecimal={disabled}
                />
              </FloatingFormItem>
            </Col>
          </Tooltip>

          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'release.form.netValue',
              })}
              name="totalAmount"
              initialValue={0}
            >
              <InputDecimal disabled />
            </FloatingFormItem>
          </Col>

          <Divider />

          {!!installment?.id && (
            <Col span={24}>
              <AccountPayableObservationList
                installmentPayableId={installment?.id}
              />
            </Col>
          )}
        </Row>
      </Form>

      <EmployeesSelectedModal
        visible={modalEmployeeVisible}
        handleVisible={modalEmployeeAction.toggle}
        noSelection
        onEmployeeSelect={handleEmployeeSelect}
      />

      <SuppliersSelectedModal
        visible={modalSupplierVisible}
        handleVisible={modalSupplierAction.toggle}
        noSelection
        onSupplierSelect={handleSupplierSelect}
      />
    </>
  );
}

export default ReleaseForm;
